/**
 * Module dependencies.
 */

import { TransactionBadge } from 'app/types/transaction';
import { resolveAppUrl } from 'app/core/utils/url-resolver';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRequestProvider } from 'app/components/providers/request-provider/request-provider';

/**
 * Query key for the user's badge library.
 */

const BADGES_KEY = ['transactionBadges'];

/**
 * Invalidate the badge library and the transactions list (badge chips live on
 * transaction rows, so rename/recolor/delete must refresh both).
 */

function useInvalidate() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries(BADGES_KEY);
    queryClient.invalidateQueries(['transactions']);
  };
}

/**
 * Export `useTransactionBadges` — the user's badge library.
 */

export function useTransactionBadges() {
  const client = useRequestProvider();

  return useQuery<TransactionBadge[]>(BADGES_KEY, {
    queryFn: async () =>
      client.get(resolveAppUrl('transactionBadges')).then(({ data }: { data: any }) => data?.data ?? [])
  });
}

/**
 * Export `useCreateBadge`.
 */

export function useCreateBadge() {
  const client = useRequestProvider();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: async (payload: { name: string; color: string }): Promise<TransactionBadge> =>
      client.post(resolveAppUrl('transactionBadges'), payload).then(({ data }: any) => data?.data),
    onSuccess: invalidate
  });
}

/**
 * Export `useUpdateBadge` — rename and/or recolor.
 */

export function useUpdateBadge() {
  const client = useRequestProvider();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: async ({ id, ...payload }: { id: number } & Partial<Pick<TransactionBadge, 'name' | 'color'>>) =>
      client
        .patch(resolveAppUrl('transactionBadgesById', { interpolations: { id } }), payload)
        .then(({ data }: any) => data?.data),
    onSuccess: invalidate
  });
}

/**
 * Export `useDeleteBadge`.
 */

export function useDeleteBadge() {
  const client = useRequestProvider();
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: async (id: number) =>
      client.delete(resolveAppUrl('transactionBadgesById', { interpolations: { id } })).then(({ data }: any) => data),
    onSuccess: invalidate
  });
}

/**
 * Export `useAttachBadge` — link a badge to a transaction.
 */

export function useAttachBadge() {
  const client = useRequestProvider();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ transactionId, badgeId }: { transactionId: string | number; badgeId: number }) =>
      client
        .post(resolveAppUrl('transactionBadgeAttach', { interpolations: { id: transactionId, badgeId } }))
        .then(({ data }: any) => data),
    onSuccess: () => queryClient.invalidateQueries(['transactions'])
  });
}

/**
 * Export `useDetachBadge` — unlink a badge from a transaction.
 */

export function useDetachBadge() {
  const client = useRequestProvider();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ transactionId, badgeId }: { transactionId: string | number; badgeId: number }) =>
      client
        .delete(resolveAppUrl('transactionBadgeAttach', { interpolations: { id: transactionId, badgeId } }))
        .then(({ data }: any) => data),
    onSuccess: () => queryClient.invalidateQueries(['transactions'])
  });
}
